import { For, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import { TIER_LABEL_KEY } from '../../lib/notification-labels';
import {
  deliverySentAt,
  deliveryWhyKey,
  groupDeliveriesByTier,
  type NotificationDelivery,
} from '../../lib/notification-recent';
import { absolute, ago } from '../../lib/time';

// The recent list's PRESENTER half (notifications overhaul, slice 21): pure
// rows in, grouped rows out. Every decision about the data lives one module
// down (`lib/notification-recent.ts`): the tier grouping, and the one map from
// a `why` code to the sentence. This file owns only layout.
//
// Each row is one line: what it was (its title), what we did with it and why
// (the sentence), and — only for a row that went out — when, as a machine-
// readable `<time>` whose visible face is the age and whose tooltip is the
// clock (`lib/time.ts` owns both spellings). A row that did not go out has no
// time to show and shows none; a blank timestamp cell would imply a promise of
// one ("soon", "?") that nothing here means.

export function NotificationRecentList(props: {
  readonly rows: readonly NotificationDelivery[];
}): JSX.Element {
  return (
    // No class on the wrapper: it groups the `<For/>` output and paints
    // nothing — spacing between groups is the sibling rule in the sheet.
    <div>
      <For each={groupDeliveriesByTier(props.rows)}>
        {(group) => (
          <section class="notification-recent__group" aria-label={t(TIER_LABEL_KEY[group.tier])}>
            <h3 class="notification-recent__tier">{t(TIER_LABEL_KEY[group.tier])}</h3>
            <ul class="row-list">
              <For each={group.rows}>
                {(row) => (
                  <li class="notification-recent__row">
                    <p class="notification-recent__title">{row.title}</p>
                    <p class="notification-recent__why">{t(deliveryWhyKey(row))}</p>
                    <Show when={deliverySentAt(row)} keyed>
                      {(sentAt) => (
                        <time
                          class="notification-recent__when"
                          datetime={sentAt}
                          title={absolute(sentAt)}
                        >
                          {ago(sentAt)}
                        </time>
                      )}
                    </Show>
                  </li>
                )}
              </For>
            </ul>
          </section>
        )}
      </For>
    </div>
  );
}
